import { action, computed, observable } from "mobx";
import { startOfDay, isEqual as isEqualDate, startOfToday } from "date-fns";
import moment from "moment";
import { DateTime } from "luxon";
import { isEmpty, isEqual, differenceBy } from "lodash";
import { FormErrors, FormStore } from "@modules/formx";
import { localToImplicit, reviveDates } from "@bonliva-crm/common";
import { bookingCreationBlockedRecruitingPortal } from "@bonliva-crm/shared";
import { store } from "@store";
import locale from "@bonliva-crm/locale";
import {
  BookingAuditSearch_Get,
  BookingEdit_Edit,
  BookingEdit_Create,
  BookingEdit_FetchDepts,
  BookingEdit_Get,
  BookingEdit_GetConsultantContracts,
  BookingEdit_GetCustomerContracts,
  BookingEdit_CreateDirectRequestContract,
  BookingEdit_NpDirectRequestAvailable,
  BookingEditFragment,
  BookingEdit_BookingGroupFragment,
  BookingFinanceSearch_Get,
  BookingScheduleSearch_Get,
  ConsultantContractListItem,
  ContractType,
  CustomerContractListItem,
  LegalPersonType,
  BookingEdit_GetConsultant,
  BookingEdit_GetCustomer,
  BookingEdit_CreateMutation,
  BookingEdit_GetAllowedCostCenters,
  CostCenter
} from "@graphql";
import { Consultant } from "@containers/VirtualSelector/ConsultantSelector";
import { Customer } from "@containers/VirtualSelector/CustomerSelector";
import {
  FormData,
  formDataToGql,
  initialValues,
  validationSchema,
  yesNoToBoolean
} from "./utils";
import { ProjectSelectorStore } from "../ProjectSelector/ProjectSelector";
import {
  CONTRACT_CONTRACT_SUBVERSION_SLIDER,
  CustomerContractSubVersionEditorLocation
} from "@navigation";
import { customerContractOptionDescription } from "@bookings/BookingEdit/BookingEditForm";
import { CustomerContractProfileLocation } from "../../contracts/locations/CustomerContractProfileLocation";
import { useConfirmDialog } from "@hooks";
import { OkDialogOptions } from "@containers/ConfirmDialog/OkDialogOptions";
import { YesNoCancelDialogOptions } from "@containers";

const CONTRACT_REQUIRED_COST_CENTERS = [
  "10",
  "15",
  "20",
  "21",
  "22",
  "25",
  "30",
  "31",
  "40",
  "50",
  "52",
  "61",
  "62",
  "63",
  "65",
  "66",
  "67",
  "68",
  "69",
  "81",
  "82",
  "83"
];

export class BookingEditStore extends FormStore<FormData> {
  @observable isLoadingDepartments = false;
  @observable allDepartments: LabelValue[] = [];
  @observable allConsultantContracts: ConsultantContractListItem[] = [];
  @observable allCustomerContracts: CustomerContractListItem[] = [];
  @observable allSubVersionsContracts: CustomerContractListItem[] = [];
  @observable subVersionsContracts: CustomerContractListItem[] = [];
  @observable consultantData: Consultant = null;
  @observable bookingData: BookingEditFragment;
  @observable bookingGroup: BookingEdit_BookingGroupFragment;
  @observable costCenters: CostCenter[] = [];
  @observable fetchError: string = null;

  projectSelectorStore: ProjectSelectorStore = new ProjectSelectorStore((id) => this.setValue("ProjectId", id));

  @observable private _isOpen = false;
  @observable private _selectedCustomer: Partial<Customer> | null = null;
  @observable private _customerNumber: string | null = null;
  @observable private _dateStart: Date = moment.utc().startOf("isoWeek").toDate();
  @observable private _travelInvoicingDateStart: Date = moment
    .utc()
    .startOf("isoWeek")
    .toDate();

  private isNationalProcurementSet = false;
  private customerContractsAbortController: AbortController | null = null;
  private consultantContractsAbortController: AbortController | null = null;

  constructor(
    private onClose: (id?: string) => void,
    private id: string,
    private initialData: Partial<FormData>,
    private duplicateFrom: string
  ) {
    super({ schema: validationSchema, validate: () => this._validate() });

    this.onSetValue((options) => {
      if (options.name === "AreaOfExpertise") {
        this.reloadCustomerContracts();

        if (this.isNationalProcurementAvailable) {
          if (!this.isNationalProcurementSet) {
            this.setValue("isNationalProcurement", null);
            this.isNationalProcurementSet = true;
          }
        } else {
          this.setValue("isNationalProcurement", "false");
          this.isNationalProcurementSet = false;
          this.values.authorizedOrderer = null;
        }

        this.autoSelectLastWeekForTravelInvoicing();
      }

      if (options.name === "isNationalProcurement") {
        this.resetContractRelatedValues();
        this.values.authorizedOrderer = null;

        this.setValue("ContractType", null);

        this.autoSelectLastWeekForTravelInvoicing();

        this._validate();
      }

      if (options.name === "nationalProcurementTravelRequired") {
        this.setValue("travelTemplateLevel", null);

        this.autoSelectLastWeekForTravelInvoicing();
      }

      if (options.name === "consultantContractId") {
        if (options.value) {
          const findContract = this.allConsultantContracts.find(
            (x) => x._id === options.value
          );

          if (findContract) {
            this.setValue("ContractType", findContract.SalaryChoice);
          }
        } else {
          this.setValue("ContractType", null);
        }
      }

      if (options.name === "ContractType") {
        if (
          this.values.ContractType === null &&
          this.values.isNationalProcurement === "true" &&
          this.values.LegalPersonType === LegalPersonType.Employee
        ) {
          this.setValue("ContractType", ContractType.NationalProcurement);
        }
      }

      if (options.name === "LegalPersonType") {
        if (
          options.value === LegalPersonType.Employee &&
          this.values.isNationalProcurement === "true"
        ) {
          this.setValue("ContractType", ContractType.NationalProcurement);
        } else {
          this.setValue("ContractType", null);
        }

        // Auto-select last week for travel invoicing if conditions are met
        this.autoSelectLastWeekForTravelInvoicing();
      }
    });

    this.init().then(() => {
      this.loading = false;

      if (this.bookingData) {
        this.getCustomerContracts();
      }

      if (this.values.HospitalRef?.value) {
        this.refreshNpDirectRequestAvailability();
      }

      if (this.values.Consultant && !this.consultantData) {
        this.getConsultantData();
      }

      if (this.values.HospitalRef && !this._selectedCustomer) {
        this.getCustomerData();
      }

      this.getCostCentersData();
    });

    (window as any).bookingEditStore = this;
  }

  @computed get isNew() {
    return !this.id;
  }

  @computed get disabledAuthorizedOrderer() {
    return this.bookingGroup?.hasClosedBooking && !this.duplicateFrom;
  }

  @computed get callNumberTooltip() {
    const isBleking = this._selectedCustomer?.Parents?.some(
      (x) => x.Name === "Region Blekinge"
    );

    const isGavleborg = this._selectedCustomer?.Parents?.some(
      (x) => x.Name === "Region Gävleborg"
    );

    if (isBleking) {
      return locale.CallNumberTooltipForRegionBleking;
    }

    if (isGavleborg) {
      return locale.CallNumberTooltipForRegionGavleborg;
    }

    return locale.CallNumberTooltip;
  }

  @computed get isRequiredCallNumber() {
    if (this.values.isNationalProcurement === "true" && this._selectedCustomer) {
      const isBleking = this._selectedCustomer?.Parents?.some(
        (x) => x.Name === "Region Blekinge"
      );

      const isGavleborg = this._selectedCustomer?.Parents?.some(
        (x) => x.Name === "Region Gävleborg"
      );

      if (!isBleking && !isGavleborg) {
        return true;
      }
    }

    return false;
  }

  @computed get disabledCustomerContract() {
    return !(
      !isEmpty(this.values.Weeks) &&
      this.values.AreaOfExpertise &&
      this.values.Consultant &&
      this.values.HospitalRef
    );
  }

  @computed get isFirstWeek() {
    return this.bookingData?.isFirstWeek ?? null;
  }

  @computed get hasOpenBookings() {
    return this.bookingGroup?.bookings.filter((x) => x.isOpen).length > 0;
  }

  @computed get isUpdating() {
    return !!this.id;
  }

  @computed get isDuplicating() {
    return !!this.duplicateFrom;
  }

  @computed get isOpen() {
    return this._isOpen;
  }

  @computed get legalPersonType() {
    return this.consultantData?.LegalPersonType;
  }

  @computed get isContractTypeEnabled() {
    return (
      this.values.Weeks.length &&
      (this.consultantData || this.isDuplicating) &&
      this.values.LegalPersonType === LegalPersonType.Employee
    );
  }

  @computed get allAreasOfExpertise() {
    return store()
      .cache.areasOfExp.map((a) => a.Name)
      .sortBy((x) => x);
  }

  @computed get allSpecializations() {
    return this.values.AreaOfExpertise
      ? store().cache.getSpecializationsOfExpertises([this.values.AreaOfExpertise])
      : [];
  }

  @computed get dateSelectorDates() {
    return {
      start: moment.utc(this._dateStart).startOf("isoWeek").toDate(),
      end: moment.utc(this._dateStart).startOf("isoWeek").add(72, "weeks").toDate()
    };
  }

  @computed get dateTravelInvoicingSelectorDates() {
    return {
      start: moment.utc(this._travelInvoicingDateStart).startOf("isoWeek").toDate(),
      end: moment
        .utc(this._travelInvoicingDateStart)
        .startOf("isoWeek")
        .add(72, "weeks")
        .toDate()
    };
  }

  @computed get canSubmitForm() {
    return this.canSubmit && !!this._customerNumber && !this.blockedStaffingPortal; // && !isEmpty(this._contactPersons);
  }

  /**
   * The blocked staffing portal ("Bemanningsportal") the picked customer's organisation is on —
   * Bemlo or Medlo, see `BOOKING_CREATION_BLOCKED_RECRUITING_PORTALS` — or `null` when the
   * booking may be created here. Such a customer gets its bookings through the portal and the
   * ERP import, never from this form (CRMDEV-7351, CRMDEV-7359, ERP-1489). Known the moment
   * the customer is picked — the picker fragment carries `RecruitingPortalType`, which the
   * server resolves from the root organisation — so the form says so before anything else is
   * filled in. Only a booking made from scratch is refused: an existing booking, and a
   * duplicate of one ("Duplicera"), already belong to the customer — the ERP import created
   * the original — so there only a change of customer is blocked (CRMDEV-7364). The server
   * enforces the same rule.
   */
  @computed get blockedStaffingPortal(): string | null {
    if (!store().cache.blockBookingCreationForBemloCustomers) {
      return null;
    }

    if (!((this.isNew && !this.isDuplicating) || this.isHospitalChanged)) {
      return null;
    }

    return bookingCreationBlockedRecruitingPortal(this._selectedCustomer?.RecruitingPortalType);
  }

  /** The picked hospital differs from the one the booking (or the booking being duplicated) was loaded with. */
  @computed private get isHospitalChanged() {
    const initialId = this.initialValues.HospitalRef?.value;
    const currentId = this.values.HospitalRef?.value;

    return !initialId || String(initialId) !== String(currentId);
  }

  @computed get showMissingCustomerNumberError() {
    return !!this.values.HospitalRef && !this._customerNumber;
  }

  @computed get isConsultantContractRequired() {
    return (
      CONTRACT_REQUIRED_COST_CENTERS.includes(this.values.CostCenter) &&
      !this.isUpdating &&
      !this.values.consultantContractId
    );
  }

  @computed get showDiscountQuestion() {
    const aoeProfessions = store().cache.getProfessionsOnAreasofExp([
      this.values.AreaOfExpertise
    ]);

    if (aoeProfessions[0] !== "Läkare") {
      return false;
    }

    if (this.values.Consultant === null) {
      return false;
    }

    if (this.consultantData?.AreasOfExpertise.includes(this.values.AreaOfExpertise)) {
      return false;
    }

    return this.values.isNationalProcurement === "true";
  }

  @computed get customerContractsOptions() {
    let contracts: CustomerContractListItem[];

    if (this.values.isNationalProcurement === "true") {
      contracts = this.allCustomerContracts.filter((x) => x.isNationalProcurement);
    } else {
      contracts = this.allCustomerContracts.filter((x) => !x.isNationalProcurement);
    }

    const options = contracts.map((x) => ({
      label: x.contractName,
      value: x._id,
      divider: true,
      description: customerContractOptionDescription(x.priceLists)
    }));

    const selectedCustomerContract = this.allCustomerContracts?.find(
      (x) => x._id === this.values.customerContractId
    );

    if (this.values.customerContractId && !selectedCustomerContract) {
      const savedCustomerContract =
        this.bookingData?.BookingGroup?.customerContract ?? null;
      if (savedCustomerContract) {
        options.push({
          label: savedCustomerContract.contractName,
          value: savedCustomerContract._id,
          divider: true,
          description: customerContractOptionDescription(savedCustomerContract.priceLists)
        });
      }
    }

    return options;
  }

  @computed get consultantContractsOptions() {
    const nationalContractSuffix = "_National";
    let contracts: ConsultantContractListItem[];

    if (this.values.isNationalProcurement === "true") {
      contracts = this.allConsultantContracts.filter((option) =>
        option.Type.endsWith(nationalContractSuffix)
      );
    } else {
      contracts = this.allConsultantContracts.filter(
        (option) => !option.Type.endsWith(nationalContractSuffix)
      );
    }

    return contracts.map((option) => ({
      label: option.Name,
      value: option._id,
      divider: true,
      description: option.Period
    }));
  }

  @computed get subVersionsContractsOptions() {
    return (
      this.subVersionsContracts.toLabelValue(
        (x) => x.contractName,
        (x) => x._id
      ) ?? []
    );
  }

  @computed get contractTypes(): ContractType[] {
    if (this.values.consultantContractId) {
      return Object.values(ContractType);
    }

    if (this.values.isNationalProcurement === "true") {
      return Object.values(ContractType);
    }

    return Object.values(ContractType).filter(
      (type) => type !== ContractType.NationalProcurement
    );
  }

  @computed get isExtendedBookingGroup() {
    return !isEqual(
      this.initialValues.Weeks.sort(),
      this.values.Weeks.map((x) => (typeof x === "string" ? x : x.toISOString())).sort()
    );
  }

  @computed get isNewWeeksAdded(): boolean {
    return (
      differenceBy(this.values.Weeks, this.initialValues.Weeks, (x) =>
        typeof x === "string" ? new Date(x).getTime() : x.getTime()
      ).length > 0
    );
  }

  @computed get hasCallNumber() {
    return this.values.callNumber;
  }

  @computed get selectedCustomerContract() {
    let contract = this.allCustomerContracts?.find(
      (x) => x._id === this.values.customerContractId
    );

    if (
      !contract &&
      this.bookingData?.BookingGroup?.customerContract &&
      !this.isDuplicating
    ) {
      contract = this.bookingData.BookingGroup.customerContract;
    }

    return contract;
  }

  @computed get isNationalProcurementAvailable() {
    const allowedProfessions = ["Läkare", "SSK", "Röntgensjuksköterska", "Barnmorska"];

    const aoeProfessions = store().cache.getProfessionsOnAreasofExp([
      this.values.AreaOfExpertise
    ]);

    return allowedProfessions.includes(aoeProfessions[0]);
  }

  @computed get isMainFormAvailable() {
    if (!this.isNew) {
      return true;
    }

    if (this.values.AreaOfExpertise) {
      if (this.isNationalProcurementAvailable) {
        return Boolean(this.values.isNationalProcurement);
      }
      return true;
    }

    return false;
  }

  @computed get isCustomerContractRequired() {
    return (
      (this.values.isNationalProcurement === "true" && !this.values.customerContractId) ||
      (CONTRACT_REQUIRED_COST_CENTERS.includes(this.values.CostCenter) &&
        !this.isUpdating &&
        !this.values.customerContractId)
    );
  }

  @computed get isNationalProcurementTravelQuestion(): boolean {
    if (this.values.isNationalProcurement === "false") {
      return false;
    }

    if (!this._selectedCustomer?.Address?.Region) {
      return false;
    }

    return store().cache.nationalProcurementTravelRegions.includes(
      this._selectedCustomer.Address.Region
    );
  }

  @computed get shouldShowTravelInvoicing(): boolean {
    // Check if it's a national procurement booking
    if (this.values.isNationalProcurement !== "true") {
      return false;
    }

    // Check if user selected "Yes" for travel invoicing
    if (this.values.nationalProcurementTravelRequired !== "true") {
      return false;
    }

    // Check if user has selected booking weeks
    if (!this.values.Weeks || this.values.Weeks.length === 0) {
      return false;
    }

    // Check if customer is in one of the northern regions
    if (!this._selectedCustomer?.Address?.Region) {
      return false;
    }

    return [
      "Jämtlands län",
      "Norrbottens län",
      "Västerbottens län",
      "Västernorrlands län"
    ].includes(this._selectedCustomer.Address.Region);
  }

  getCostCentersData = () => {
    BookingEdit_GetAllowedCostCenters.func({}, { fetchPolicy: "network-only" }).then(
      (data) => {
        this.costCenters = data.data.me.allowedCostCenters.filter((x) => x.IsActive);
      }
    );
  };

  get disabledWeeks() {
    if (!this.bookingData) {
      return [];
    }

    if (!this.bookingGroup) {
      return [];
    }

    if (this.isDuplicating) {
      return [];
    }

    return this.bookingGroup.bookings.filter((x) => x.isCancellable).map((x) => x.Week);
  }

  init = async () => {
    try {
      const idToFetch = this.id || this.duplicateFrom;

      if (idToFetch) {
        const {
          data: { bookingById: data }
        } = await BookingEdit_Get.func(
          { id: idToFetch },
          { fetchPolicy: "network-only" }
        );

        this.bookingData = reviveDates(data);
        this.bookingGroup = reviveDates(data.BookingGroup);

        if (data.Consultant._id) {
          this.getConsultantContracts(
            data.Consultant._id,
            data.BookingGroup.bookings.map((x) => x.Week)
          );
        }

        const initialValues: FormData = {
          AreaOfExpertise: data.AreaOfExpertise,
          BusinessName: data.ConsultantSnapshot.BusinessName,
          consultantContractId: data.BookingGroup.consultantContractId,
          customerContractId: data.BookingGroup.customerContractId,
          subVersionId: data.BookingGroup.subVersionId,
          isNationalProcurement: data.BookingGroup.isNationalProcurement
            ? "true"
            : "false",
          BusinessSocialSecurityNo: data.ConsultantSnapshot.BusinessSocialSecurityNo,
          Consultant: {
            label: data.Consultant._id,
            value: data.Consultant._id
          },
          CostCenter: data.CostCenter ? data.CostCenter.Id : null,
          ContractType: data.ContractType,
          DepartmentsRefs: data.BookingGroup.department
            ? [data.BookingGroup.department._id]
            : [],
          HospitalRef: {
            label: data.BookingGroup.hospital.Name,
            value: data.BookingGroup.hospital._id
          },
          LegalPersonType: data.ConsultantSnapshot.LegalPersonType,
          ProjectId: data.Project._id,
          Specializations: data.specializations,
          Weeks: data.BookingGroup.bookings.map((x) => x.Week),
          UpdateConsultantWithSnapshotData: false,
          bookingIntro: {
            ...data.bookingIntro,
            required: data.bookingIntro ? "true" : "false"
          },
          callNumber: this.isDuplicating ? "" : data.callNumber,
          authorizedOrderer: data.BookingGroup.authorizedOrderer,
          isDiscount: data.BookingGroup.isDiscount ? "true" : "false",
          nationalProcurementTravelRequired: data.BookingGroup
            .nationalProcurementTravelRequired
            ? "true"
            : "false",
          travelTemplateLevel: data.BookingGroup.travelTemplateLevel,
          travelInvoicingWeeks: data.BookingGroup.bookings
            .filter((x) => x.requireTravelInvoicing)
            .map((x) => x.Week)
        };

        if (this.duplicateFrom) {
          this.reset({
            ...initialValues,
            isNationalProcurement: null,
            consultantContractId: null,
            customerContractId: null,
            Weeks: []
          });
        } else {
          this.reset(initialValues);
        }
      } else {
        this.reset({
          ...initialValues,
          ...this.initialData
        });
      }

      this.fetchHospitalData();
      // await this.getCustomerContracts();
    } catch (e) {
      this.fetchError = e.toString();
    }
  };

  onSubmit = async () => {
    if (this.isExtendedBookingGroup) {
      if (this.hasCallNumber) {
        const confirmResult = await store().ui.confirmNullifyAsync(
          this.isNew
            ? locale.ConfirmNewBookingCallNumber
            : locale.ConfirmBookingCallNumber
        );

        if (!confirmResult) {
          return;
        }
      }

      if (!this.isNew && this.isNationalProcurementAvailable) {
        const { confirm } = useConfirmDialog();
        const confirmResult = await confirm(
          new YesNoCancelDialogOptions(locale.NationalProcurementAlert1)
        );

        if (confirmResult === "cancel") {
          return;
        }

        const chosenOption = yesNoToBoolean(confirmResult);
        if (String(chosenOption) !== this.values.isNationalProcurement) {
          await confirm(new OkDialogOptions(locale.NationalProcurementAlert2));
          return;
        }
      }

      if (
        !this.isNew &&
        this.values.isNationalProcurement === "true" &&
        this.isNewWeeksAdded
      ) {
        const { confirm } = useConfirmDialog();
        const confirmResult = await confirm(
          new YesNoCancelDialogOptions(locale.NationalProcurementAlert3)
        );

        if (confirmResult === "cancel") {
          return;
        }

        if (!yesNoToBoolean(confirmResult)) {
          return;
        }
      }
    }

    return this._submit();
  };

  _validate = () => {
    const result: FormErrors<FormData & { datesOutOfContract: string }> = {};

    if (this.isContractTypeEnabled && !this.values.ContractType) {
      result.ContractType = locale.ContractType;
    }

    if (!this.values.callNumber) {
      if (this.isRequiredCallNumber) {
        result.callNumber = locale.CallNumberLabel;
      }
    }

    if (this.isCustomerContractRequired) {
      result.customerContractId = locale.CustomerContracts;
    }

    if (this.isConsultantContractRequired) {
      result.consultantContractId = locale.ConsultantContracts;
    }

    if (
      this.isNew &&
      this.values.isNationalProcurement === "true" &&
      !this.values.authorizedOrderer
    ) {
      result.authorizedOrderer = locale.AuthorizedOrderer;
    }

    if (this.showDiscountQuestion && !this.values.isDiscount) {
      result.isDiscount = locale.TenPercentDiscountQuestion;
    }

    if (this.shouldShowTravelInvoicing && this.values.travelInvoicingWeeks.length === 0) {
      result.travelInvoicingWeeks = locale.AtLeastOneTravelInvoicingWeekRequired;
    }

    const customerContract = this.selectedCustomerContract;
    if (customerContract) {
      // Direct-request contracts are Regular-typed but selectable by execution
      // date (like consultant booking contracts), so validate their week range
      // against the booking weeks — not the booking creation date. Must match the
      // backend predicate in GetCustomerContractsHandler.isDirectRequestContract.
      const isDirectRequestContract =
        customerContract.useNationalProcurementInvoicing === true &&
        customerContract.isNationalProcurement === false;

      let dates = [];
      if (
        customerContract.contractType === "ConsultantBooking" ||
        isDirectRequestContract
      ) {
        dates = this.values.Weeks;
      } else if (customerContract.contractType === "Regular") {
        dates = this.getBookingWeeksCreationDates();
      }

      if (dates.length) {
        const datesWithinContract = dates.filter((date) => {
          const dateToCheck = moment(date);
          const contractStartWeek = moment(customerContract.contractStart).isoWeek();
          const contractStartYear = moment(customerContract.contractStart).isoWeekYear();
          const contractEndWeek = moment(customerContract.contractEnd).isoWeek();
          const contractEndYear = moment(customerContract.contractEnd).isoWeekYear();
          const targetWeek = dateToCheck.isoWeek();
          const targetYear = dateToCheck.isoWeekYear();

          // If years are different, we need to compare the full dates
          if (targetYear !== contractStartYear || targetYear !== contractEndYear) {
            return !dateToCheck.isBetween(
              moment(customerContract.contractStart).startOf("week"),
              moment(customerContract.contractEnd).endOf("week"),
              "day",
              "[]"
            );
          }

          // If years are the same, we can compare week numbers
          return targetWeek < contractStartWeek || targetWeek > contractEndWeek;
        });

        if (datesWithinContract.length) {
          result.datesOutOfContract =
            "Det går inte att lägga till valda veckor på det valda kundavtalet. Om kundavtalet är ett generellt " +
            "kundavtal beror det på att dagens datum inte är innanför start- och slutdatum för avtalet; om " +
            "kundavtalet är ett konsultspecifikt kundavtal beror det på att datumen för bokningens utförande inte är " +
            "innanför avtalets start- och slutdatum. Skapa gärna en ny bokningsgrupp för veckorna du försöker lägga till.";
        }
      }
    }

    if (
      this.isNationalProcurementTravelQuestion &&
      !this.values.nationalProcurementTravelRequired
    ) {
      result.nationalProcurementTravelRequired = locale.NationalProcurementTravelQuestion;
    }

    if (
      this.values.nationalProcurementTravelRequired === "true" &&
      !this.values.travelTemplateLevel
    ) {
      result.travelTemplateLevel = locale.TravelTemplateLevel;
    }

    return result;
  };

  fetchHospitalData = () => {
    if (!this.values.HospitalRef) {
      return;
    }

    this.isLoadingDepartments = true;

    BookingEdit_FetchDepts.func(
      { hospitalId: this.values.HospitalRef.value },
      { fetchPolicy: "network-only" }
    ).then(({ data }) => {
      this.allDepartments = data.departments.items.toLabelValue(
        (x) => x.Name,
        (x) => x._id
      );

      if (data.hospital.forceCallNumber) {
        this.validate();
      }
    });
  };

  getConsultantContracts = (consultantId, dates: Date[]) => {
    if (!consultantId) {
      return;
    }

    // Cancel any previous request
    if (this.consultantContractsAbortController) {
      this.consultantContractsAbortController.abort();
    }

    // Create new AbortController for this request
    this.consultantContractsAbortController = new AbortController();

    this.isLoadingDepartments = true;

    // Fall back to today when no weeks are selected (e.g. new/duplicated
    // booking) so the server's date filter hides expired contracts instead
    // of returning the consultant's full history.
    const effectiveDates = dates.length ? dates : [new Date()];

    BookingEdit_GetConsultantContracts.func(
      { consultantId, dates: effectiveDates },
      {
        fetchPolicy: "network-only",
        context: {
          fetchOptions: {
            signal: this.consultantContractsAbortController.signal
          }
        }
      }
    )
      .then(({ data }) => {
        this.allConsultantContracts = data.contracts.map((x) => {
          if (x.ParentId) {
            return { ...x, Name: `- ${x.Name}` };
          }
          return x;
        });
      })
      .catch((error) => {
        // Only log error if it's not an abort error
        if (error.name !== "AbortError") {
          console.error("Error fetching consultant contracts:", error);
        }
      })
      .finally(() => {
        this.isLoadingDepartments = false;
      });
  };

  getCustomerContracts = async () => {
    const areaOfExpertise = this.values.AreaOfExpertise;
    const consultantId = this.values.Consultant?.value;

    // areaOfExpertise and consultantId are non-nullable (String!) on the query
    // filter, and no contract is selectable without them (the backend returns []
    // for an unknown area of expertise). Firing before the user has picked them
    // sent null and hard-failed the whole query. Skip until both are set.
    if (!areaOfExpertise || !consultantId) {
      this.allCustomerContracts = [];
      this.allSubVersionsContracts = [];
      this.subVersionsContracts = [];
      return;
    }

    // Cancel any previous request
    if (this.customerContractsAbortController) {
      this.customerContractsAbortController.abort();
    }

    // Create new AbortController for this request
    this.customerContractsAbortController = new AbortController();

    await BookingEdit_GetCustomerContracts.func(
      {
        filter: {
          dates: this.getBookingWeeksCreationDates(),
          weeks: this.values.Weeks,
          workplaceIds: [
            this.values.HospitalRef?.value,
            ...this.values.DepartmentsRefs.map((x) => x)
          ].filter(Boolean),
          areaOfExpertise,
          consultantId,
          contractId: this.values.customerContractId || null
        }
      },
      {
        fetchPolicy: "network-only",
        context: {
          fetchOptions: {
            signal: this.customerContractsAbortController.signal
          }
        }
      }
    )
      .then(({ data }) => {
        const contracts = data.customerContractsByBooking;
        this.allCustomerContracts = contracts.filter((x) => !x.isSubVersion);
        this.allSubVersionsContracts = contracts.filter((x) => x.isSubVersion);

        this.subVersionsContracts = this.values.customerContractId
          ? this.allSubVersionsContracts.filter(
              (x) => x.parentId === this.values.customerContractId
            )
          : [];
      })
      .catch((error) => {
        // Only log error if it's not an abort error
        if (error.name !== "AbortError") {
          console.error("Error fetching customer contracts:", error);
        }
      });
  };

  getConsultantData = () => {
    if (this.values.Consultant) {
      BookingEdit_GetConsultant.func({
        id: this.values.Consultant.value
      }).then(({ data }) => {
        if (data.consultantById) {
          this.consultantData = data.consultantById;
        }
      });
    } else {
      store().snackbars.error("Can not load consultant data!");
    }
  };

  getCustomerData = () => {
    if (this.values.HospitalRef) {
      BookingEdit_GetCustomer.func({
        id: this.values.HospitalRef.value
      }).then(({ data }) => {
        if (data.customerById) {
          this._selectedCustomer = data.customerById as Customer;
          this.autoSelectLastWeekForTravelInvoicing();
        }
      });
    } else {
      store().snackbars.error("Can not load customer data!");
    }
  };

  handleCustomerContract = (contractId: string) => {
    this.subVersionsContracts = contractId
      ? this.allSubVersionsContracts.filter((x) => x.parentId === contractId)
      : [];

    this.setValue("customerContractId", contractId);
    if (!contractId) {
      this.setValue("subVersionId", null);
    }

    // Auto-fill the booking's call number (avropsnummer) from the selected
    // contract when it carries one.
    const selected = this.allCustomerContracts?.find((x) => x._id === contractId);
    if (selected?.avropsnummer) {
      this.setValue("callNumber", selected.avropsnummer);
    }
  };

  handleConsultantContract = (contractId: string) => {
    this.setValue("consultantContractId", contractId);
  };

  addSubVersion = () => {
    store().navigation.open(
      CONTRACT_CONTRACT_SUBVERSION_SLIDER,
      new CustomerContractSubVersionEditorLocation(
        this.values.customerContractId,
        null,
        "add",
        {
          consultantId: this.values.Consultant.value,
          basicPrice: null,
          startDate: null,
          endDate: null
        },
        () => {
          this.getCustomerContracts();
        }
      )
    );
  };

  // --- Direct request contract (clone of the NP contract) ---

  @observable showDirectRequestModal = false;
  @observable isCreatingDirectRequestContract = false;
  @observable lastCreatedDirectRequestContractId: string | null = null;
  // Whether the selected customer (or one of its departments) participates in the
  // tenant's National Procurement contract — required to derive a direct request.
  @observable npDirectRequestAvailable = false;

  @computed get lastCreatedDirectRequestContractUri(): string | null {
    return this.lastCreatedDirectRequestContractId
      ? new CustomerContractProfileLocation(
          this.lastCreatedDirectRequestContractId
        ).toURI()
      : null;
  }

  // Customer (hospital), area of expertise and booking weeks must all be set, and
  // the customer must participate in the NP contract, before a direct-request
  // contract can be derived.
  @computed get canCreateDirectRequestContract(): boolean {
    return Boolean(
      this.values.HospitalRef?.value &&
        this.values.AreaOfExpertise &&
        this.values.Weeks?.length &&
        this.npDirectRequestAvailable
    );
  }

  // Tooltip shown on the disabled "Skapa direktförfrågansavtal" button. The
  // missing-field hint takes precedence; once customer/AOE/weeks are set, the
  // remaining reason is that the customer is not part of the NP contract.
  @computed get directRequestButtonTooltip(): string {
    if (this.canCreateDirectRequestContract) {
      return "";
    }

    if (
      !this.values.HospitalRef?.value ||
      !this.values.AreaOfExpertise ||
      !this.values.Weeks?.length
    ) {
      return locale.directRequestContract.buttonTooltip;
    }

    return locale.directRequestContract.notInNationalProcurementTooltip;
  }

  // Refreshes npDirectRequestAvailable for the current customer + departments.
  // Only relevant in the non-national form state where the button is shown.
  refreshNpDirectRequestAvailability = () => {
    if (!this.values.HospitalRef?.value) {
      this.npDirectRequestAvailable = false;
      return;
    }

    BookingEdit_NpDirectRequestAvailable.func(
      {
        customerId: this.values.HospitalRef.value,
        departmentIds: this.values.DepartmentsRefs.map((x) => x)
      },
      { fetchPolicy: "network-only" }
    )
      .then(({ data }) => {
        this.npDirectRequestAvailable = Boolean(data?.npDirectRequestAvailable);
      })
      .catch(() => {
        this.npDirectRequestAvailable = false;
      });
  };

  // Date range (matching the selected booking weeks) shown in the modal.
  @computed get directRequestWeekRange(): string {
    if (!this.values.Weeks?.length) {
      return "";
    }
    // Mirror the backend lifecycle (CreateDirectRequestContractCase): UTC,
    // Monday-based half-open week range — start = Monday of the first week, end =
    // Monday following the last week.
    const weeks = this.values.Weeks.map((w) =>
      DateTime.fromJSDate(new Date(w), { zone: "utc" })
    );
    const start = DateTime.min(...weeks).startOf("week");
    const end = DateTime.max(...weeks)
      .startOf("week")
      .plus({ weeks: 1 });
    return `${start.toFormat("yyyy-MM-dd")} - ${end.toFormat("yyyy-MM-dd")}`;
  }

  openDirectRequestModal = () => {
    this.showDirectRequestModal = true;
  };

  closeDirectRequestModal = () => {
    this.showDirectRequestModal = false;
  };

  // Returns true only on a successful create so the modal can decide whether to
  // clear its inputs (a failed create keeps them for retry).
  createDirectRequestContract = async (input: {
    basicPrice: number;
    extraInvoiceLine: string;
    avropsnummer: string;
  }): Promise<boolean> => {
    this.isCreatingDirectRequestContract = true;

    try {
      const { data } = await BookingEdit_CreateDirectRequestContract.func({
        data: {
          customerId: this.values.HospitalRef?.value,
          departmentIds: this.values.DepartmentsRefs.map((x) => x),
          areaOfExpertise: this.values.AreaOfExpertise,
          weeks: this.values.Weeks,
          basicPrice: input.basicPrice,
          avropsnummer: input.avropsnummer,
          extraInvoiceLine: input.extraInvoiceLine
        }
      });

      const result = data.createDirectRequestContract;

      if (!isEmpty(result.errors) || !result.value) {
        store().snackbars.error(
          `${locale.directRequestContract.createFailed}\n${(result.errors ?? []).join(
            "\n"
          )}`
        );
        return false;
      }

      const created = result.value;

      // Reload the selector so the new contract is present, then select it. The
      // reload can fail silently (getCustomerContracts swallows errors) or filter
      // the contract out, so only point the selector at it when it is actually in
      // the list — otherwise the selector would hold an id with no matching option.
      await this.getCustomerContracts();
      if (this.allCustomerContracts?.some((x) => x._id === created._id)) {
        this.handleCustomerContract(created._id);
      }
      if (created.avropsnummer) {
        this.setValue("callNumber", created.avropsnummer);
      }

      // Surface a link to verify the new contract (a popup window.open after an
      // await is blocked by the browser, so render a real link instead).
      this.lastCreatedDirectRequestContractId = created._id;

      this.showDirectRequestModal = false;
      store().snackbars.success(locale.directRequestContract.createSuccess);
      return true;
    } catch (error) {
      store().snackbars.error(locale.directRequestContract.createFailed);
      return false;
    } finally {
      this.isCreatingDirectRequestContract = false;
    }
  };

  getBookingWeeksCreationDates = (): Date[] => {
    if (!this.bookingData) {
      return this.values.Weeks.length ? [localToImplicit(startOfToday())] : [];
    }

    const newWeeksAdded = this.bookingData.BookingGroup.bookings.some(
      (booking) =>
        !this.values.Weeks.some((week) => {
          const bookingDate = startOfDay(booking.Week);
          const existingDate = startOfDay(week);
          return isEqualDate(bookingDate, existingDate);
        })
    );

    let result = this.bookingData.BookingGroup.bookings.map((x) => x.createdOn);

    if (newWeeksAdded) {
      result = [...result, startOfToday()];
    }

    return result;
  };

  private resetContractRelatedValues() {
    this.values.customerContractId = null;
    this.values.consultantContractId = null;
  }

  @action setConsultant = (consultant: Consultant) => {
    this.consultantData = consultant;

    if (!consultant) {
      this.values.Consultant = null;
      this.values.LegalPersonType = null;
      this.values.BusinessName = "";
      this.values.BusinessSocialSecurityNo = "";
      this.values.UpdateConsultantWithSnapshotData = false;

      if (!this.id) {
        this.projectSelectorStore.removeByTag("consultantBA");
      }

      this.validate();
      return;
    }

    this.values.Consultant = {
      label: consultant.Name,
      value: consultant._id
    };

    this.setValue("LegalPersonType", consultant.LegalPersonType);

    if (this.values.LegalPersonType === LegalPersonType.Subcontractor) {
      this.values.BusinessName = consultant.BusinessName;
      this.values.BusinessSocialSecurityNo = consultant.BusinessSocialSecurityNo;
    }

    if (consultant.ResponsiblePerson) {
      this.projectSelectorStore.addUser(
        {
          _id: consultant.ResponsiblePerson.PersonId,
          Name: consultant.ResponsiblePerson.Name,
          AvatarPic: ""
        },
        "consultantBA"
      );
    }

    this.values.UpdateConsultantWithSnapshotData = !this.consultantData.LegalPersonType;

    this.validate();

    this.getConsultantContracts(this.values.Consultant.value, this.values.Weeks);
    this.reloadCustomerContracts();

    this.autoSelectLastWeekForTravelInvoicing();
  };

  @action setPeriod = (weeks: Date[]) => {
    this.values.Weeks = weeks;

    this.autoSelectLastWeekForTravelInvoicing();

    this.validate();
    this.getConsultantContracts(this.values.Consultant.value, this.values.Weeks);
    this.getCustomerContracts();
  };

  @action setHospital = (hospital: Partial<Customer>) => {
    this._selectedCustomer = hospital;

    if (!hospital) {
      this.values.HospitalRef = null;
      this.values.DepartmentsRefs = [];
      this.projectSelectorStore.removeByTag("customerBA");
      this.refreshNpDirectRequestAvailability();
      this.validate();
      return;
    }

    this.values.HospitalRef = { label: hospital.Name, value: hospital._id };
    this.values.DepartmentsRefs = [];
    this.values.nationalProcurementTravelRequired = null;
    this.values.travelTemplateLevel = null;

    this.fetchHospitalData();
    this.reloadCustomerContracts();
    this.refreshNpDirectRequestAvailability();

    // Auto-select last week for travel invoicing if conditions are met
    this.autoSelectLastWeekForTravelInvoicing();

    this.validate();
  };

  @action setDepartments = (depts: string[]) => {
    this.values.DepartmentsRefs = depts;
    this.refreshNpDirectRequestAvailability();
  };

  @action setDateBack = () => {
    this._dateStart = moment.utc(this._dateStart).subtract(36, "weeks").toDate();
  };

  @action setDateFwd = () => {
    this._dateStart = moment.utc(this._dateStart).add(36, "weeks").toDate();
  };

  @action setTravelInvoicingDateBack = () => {
    this._travelInvoicingDateStart = moment
      .utc(this._travelInvoicingDateStart)
      .subtract(36, "weeks")
      .toDate();
  };

  @action setTravelInvoicingDateFwd = () => {
    this._travelInvoicingDateStart = moment
      .utc(this._travelInvoicingDateStart)
      .add(36, "weeks")
      .toDate();
  };

  @action setCustomerNumber = (v: string) => {
    this._customerNumber = v;
  };

  @action setTravelInvoicingWeeks = (weeks: Date[]) => {
    this.values.travelInvoicingWeeks = weeks;
  };

  private autoSelectLastWeekForTravelInvoicing() {
    if (this.shouldShowTravelInvoicing && this.values.Weeks.length > 0) {
      if (
        !this.values.travelInvoicingWeeks ||
        this.values.travelInvoicingWeeks.length === 0
      ) {
        const lastWeek = this.values.Weeks[this.values.Weeks.length - 1];
        this.values.travelInvoicingWeeks = [lastWeek];
      }
    }
  }

  private reloadCustomerContracts() {
    const readyToQueryCustomerContracts =
      this.values.HospitalRef && this.values.Consultant && this.values.Weeks.length > 0;

    if (!readyToQueryCustomerContracts) {
      return;
    }

    this.values.customerContractId = null;
    this.getCustomerContracts();
  }

  private async _submit(): Promise<void> {
    try {
      const refetchQueries = [
        BookingFinanceSearch_Get.OperationName,
        BookingAuditSearch_Get.OperationName,
        BookingScheduleSearch_Get.OperationName
      ];

      const input = formDataToGql(this.values);

      const result = this.isNew
        ? await BookingEdit_Create.func(
            { input, duplicateFromBookingId: this.duplicateFrom || null },
            { refetchQueries }
          )
        : await BookingEdit_Edit.func(
            {
              bookingGroupId: this.bookingGroup._id,
              input
            },
            { refetchQueries }
          );

      const getFirstBookingId = (result: any) => {
        const data = result?.data as BookingEdit_CreateMutation;
        return data?.createBookingGroup?.bookings[0]?._id;
      };

      // Reset form and trigger onClose
      this.reset(initialValues);
      this.onClose(this.isNew ? getFirstBookingId(result) : this.id);

      // Show success message
      store().snackbars.success(
        this.isNew ? locale.BookingsCreated : locale.BookingsUpdated
      );
    } catch (e) {
      store().snackbars.error(`${locale.CannotSaveBooking} ${e}`);
      throw e;
    }
  }
}
